import { Link } from '@inertiajs/react'
import SeoHead from '@/components/SeoHead'
import MarketingFooter from '@/components/MarketingFooter'

// ─── Browser Chrome wrapper for mockup screenshots ───────────────────────────

function BrowserFrame({ url, children }: { url: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-[#e2e0d8] shadow-2xl overflow-hidden bg-white">
      <div className="flex items-center gap-1.5 px-4 py-2.5 bg-[#f1f0ec] border-b border-[#e2e0d8]">
        <div className="w-3 h-3 rounded-full bg-[#ff5f57]" />
        <div className="w-3 h-3 rounded-full bg-[#febc2e]" />
        <div className="w-3 h-3 rounded-full bg-[#28c840]" />
        <div className="ml-3 flex-1 text-xs text-[#8c95a6] bg-white rounded px-3 py-1 border border-[#e2e0d8] truncate">
          {url}
        </div>
      </div>
      <div className="p-6">{children}</div>
    </div>
  )
}

// ─── Email Chrome wrapper for mockup ─────────────────────────────────────────

function EmailFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-[#e2e0d8] shadow-2xl overflow-hidden bg-white">
      <div className="flex items-center gap-1.5 px-4 py-2.5 bg-[#f1f0ec] border-b border-[#e2e0d8]">
        <div className="w-3 h-3 rounded-full bg-[#ff5f57]" />
        <div className="w-3 h-3 rounded-full bg-[#febc2e]" />
        <div className="w-3 h-3 rounded-full bg-[#28c840]" />
        <div className="ml-3 text-xs text-[#8c95a6]">Mail</div>
      </div>
      {children}
    </div>
  )
}

// ─── Mockup #1: Lender Deal Room view ────────────────────────────────────────

function MockupLenderView() {
  const commitments = [
    { name: 'Margaret Chen', amount: '$50,000', time: '2 days ago' },
    { name: 'Paulson Family Trust', amount: '$40,000', time: '1 day ago' },
    { name: 'David Rothberg', amount: '$25,000', time: '6 hours ago' },
    { name: 'Linda Park', amount: '$22,000', time: '38 min ago' },
  ]

  return (
    <BrowserFrame url="app.lendsolo.com/deal_offerings/42">
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2.5 mb-1">
              <h3 className="text-lg font-bold text-[#0f1a2e]">Milwaukee Duplex &mdash; Fix &amp; Flip</h3>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-[#e8f5ee] text-[#1a7a4c] uppercase tracking-wide">
                Open
              </span>
            </div>
            <p className="text-sm text-[#5a6578]">1847 N 24th St, Milwaukee, WI &middot; 3BR duplex</p>
          </div>
        </div>

        {/* Deal stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Principal', value: '$185,000' },
            { label: 'Rate', value: '11.5%' },
            { label: 'Term', value: '12 months' },
            { label: 'LTV', value: '68%' },
          ].map((s) => (
            <div key={s.label} className="bg-[#f8f7f4] rounded-lg px-3.5 py-2.5">
              <p className="text-[10px] font-mono uppercase tracking-wider text-[#8c95a6]">{s.label}</p>
              <p className="text-sm font-semibold text-[#0f1a2e]">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Capacity bar */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-sm font-medium text-[#0f1a2e]">Capacity</p>
            <p className="text-sm text-[#5a6578]">$137,000 of $185,000 committed</p>
          </div>
          <div className="w-full h-3 bg-[#e8f5ee] rounded-full overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: '74%',
                background: 'linear-gradient(90deg, #1a7a4c, #34D399)',
              }}
            />
          </div>
          <p className="text-xs text-[#8c95a6] mt-1">74% filled &middot; Minimum commitment: $10,000</p>
        </div>

        {/* Commitments table */}
        <div className="border border-[#e2e0d8] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#f8f7f4] text-left">
                <th className="px-4 py-2.5 font-medium text-[#5a6578] text-xs uppercase tracking-wider">Investor</th>
                <th className="px-4 py-2.5 font-medium text-[#5a6578] text-xs uppercase tracking-wider">Amount</th>
                <th className="px-4 py-2.5 font-medium text-[#5a6578] text-xs uppercase tracking-wider hidden sm:table-cell">Submitted</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e2e0d8]">
              {commitments.map((c) => (
                <tr key={c.name}>
                  <td className="px-4 py-3 text-[#0f1a2e] font-medium">{c.name}</td>
                  <td className="px-4 py-3 text-[#0f1a2e]">{c.amount}</td>
                  <td className="px-4 py-3 text-[#8c95a6] hidden sm:table-cell">{c.time}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3">
          <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1a7a4c] text-white rounded-lg text-sm font-semibold">
            Lock deal
          </span>
          <span className="inline-flex items-center gap-2 px-5 py-2.5 text-[#0f1a2e] rounded-lg text-sm font-semibold border border-[#e2e0d8]">
            Invite more investors
          </span>
        </div>
      </div>
    </BrowserFrame>
  )
}

// ─── Mockup #2: Investor invitation email ────────────────────────────────────

function MockupInvitationEmail() {
  return (
    <EmailFrame>
      {/* Email header */}
      <div className="px-6 py-4 border-b border-[#e2e0d8]">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-9 h-9 rounded-full bg-[#0f1a2e] flex items-center justify-center text-[11px] font-bold text-[#34D399]">
            PM
          </div>
          <div>
            <p className="text-sm font-semibold text-[#0f1a2e]">Pete Morrison &middot; LendSolo</p>
            <p className="text-xs text-[#8c95a6]">via lendsolo.com</p>
          </div>
        </div>
        <p className="text-sm font-medium text-[#0f1a2e]">
          You&rsquo;ve been invited to a new deal &mdash; Milwaukee Duplex
        </p>
      </div>

      {/* Email body */}
      <div className="px-6 py-5 space-y-4">
        <p className="text-sm text-[#2d3a4f] leading-relaxed">
          Hey Margaret,
        </p>
        <p className="text-sm text-[#2d3a4f] leading-relaxed">
          I&rsquo;m opening commitments on a new deal and thought it might fit your portfolio.
        </p>

        {/* Deal card in email */}
        <div className="bg-[#f8f7f4] border border-[#e2e0d8] rounded-lg p-4">
          <p className="font-semibold text-[#0f1a2e] text-sm mb-1">Milwaukee Duplex &mdash; Fix &amp; Flip</p>
          <p className="text-sm text-[#5a6578]">$185,000 &middot; 11.5% &middot; 12 months &middot; 68% LTV</p>
        </div>

        <p className="text-sm text-[#2d3a4f] leading-relaxed">
          Tap below to see the full deal and commit if you&rsquo;re in. No password needed.
        </p>

        <span className="inline-flex items-center gap-2 px-6 py-3 bg-[#1a7a4c] text-white rounded-lg text-sm font-semibold">
          View deal
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path d="M3 8h10m0 0L9 4m4 4L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>

        <p className="text-xs text-[#8c95a6] leading-relaxed">
          Commitments are non-binding until the deal is locked.
        </p>
      </div>

      {/* Email footer */}
      <div className="px-6 py-3 border-t border-[#e2e0d8] bg-[#f8f7f4]">
        <p className="text-[11px] text-[#8c95a6]">
          Sent via LendSolo &middot; This magic link expires in 7 days.
        </p>
      </div>
    </EmailFrame>
  )
}

// ─── Mockup #3: Investor-facing deal page ────────────────────────────────────

function MockupInvestorView() {
  return (
    <BrowserFrame url="app.lendsolo.com/p/a7f3x9k2">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-6">
        {/* Main content */}
        <div className="space-y-5">
          <div>
            <h3 className="text-lg font-bold text-[#0f1a2e] mb-1">Milwaukee Duplex &mdash; Fix &amp; Flip</h3>
            <p className="text-sm text-[#5a6578]">1847 N 24th St, Milwaukee, WI &middot; 3BR duplex</p>
          </div>

          {/* Deal stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { label: 'Principal', value: '$185,000' },
              { label: 'Rate', value: '11.5%' },
              { label: 'Term', value: '12 months' },
              { label: 'LTV', value: '68%' },
              { label: 'Maturity', value: 'Mar 2027' },
              { label: 'Min. commitment', value: '$10,000' },
            ].map((s) => (
              <div key={s.label} className="bg-[#f8f7f4] rounded-lg px-3.5 py-2.5">
                <p className="text-[10px] font-mono uppercase tracking-wider text-[#8c95a6]">{s.label}</p>
                <p className="text-sm font-semibold text-[#0f1a2e]">{s.value}</p>
              </div>
            ))}
          </div>

          {/* Capacity bar */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-sm font-medium text-[#0f1a2e]">Capacity</p>
              <p className="text-sm text-[#5a6578]">74% filled</p>
            </div>
            <div className="w-full h-3 bg-[#e8f5ee] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{
                  width: '74%',
                  background: 'linear-gradient(90deg, #1a7a4c, #34D399)',
                }}
              />
            </div>
          </div>

          {/* Commitment form */}
          <div className="bg-[#f8f7f4] border border-[#e2e0d8] rounded-lg p-5 space-y-3">
            <p className="text-sm font-semibold text-[#0f1a2e]">Your commitment</p>
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#5a6578]">$</span>
              <div className="flex-1 bg-white border border-[#e2e0d8] rounded-lg px-3.5 py-2.5 text-sm text-[#0f1a2e] font-medium">
                25,000
              </div>
            </div>
            <p className="text-xs text-[#8c95a6]">Minimum $10,000 &middot; $48,000 remaining capacity</p>
            <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1a7a4c] text-white rounded-lg text-sm font-semibold w-full justify-center">
              Submit soft commitment
            </span>
            <p className="text-[11px] text-[#8c95a6] text-center">
              Non-binding until the lender locks the deal.
            </p>
          </div>
        </div>

        {/* Sidebar */}
        <div className="hidden lg:block">
          <div className="bg-[#f8f7f4] border border-[#e2e0d8] rounded-lg p-4">
            <p className="text-xs font-mono uppercase tracking-wider text-[#8c95a6] mb-3">About this lender</p>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#0f1a2e] flex items-center justify-center text-xs font-bold text-[#34D399]">
                PM
              </div>
              <div>
                <p className="text-sm font-semibold text-[#0f1a2e]">Pete Morrison</p>
                <p className="text-xs text-[#8c95a6]">12 active loans</p>
              </div>
            </div>
            <p className="text-xs text-[#8c95a6]">Member since 2024</p>
          </div>
        </div>
      </div>
    </BrowserFrame>
  )
}

// ─── Lifecycle step icon ─────────────────────────────────────────────────────

function StepIcon({ step }: { step: number }) {
  const icons: Record<number, React.ReactNode> = {
    1: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="12" y1="18" x2="12" y2="12" />
        <line x1="9" y1="15" x2="15" y2="15" />
      </svg>
    ),
    2: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
      </svg>
    ),
    3: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),
    4: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0110 0v4" />
      </svg>
    ),
  }

  return (
    <div className="w-11 h-11 min-w-[44px] rounded-[10px] bg-[#e8f5ee] text-[#1a7a4c] flex items-center justify-center">
      {icons[step]}
    </div>
  )
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function DealRoom() {
  const lifecycleSteps = [
    {
      title: 'Post',
      subtitle: 'Draft the deal',
      description: 'Amount, rate, term, collateral, minimum commitment. One form, everything your investors need to know.',
    },
    {
      title: 'Invite',
      subtitle: 'Pick who sees it',
      description: 'Select which investors get the offering. They receive a magic-link email — no passwords, no accounts to create.',
    },
    {
      title: 'Commit',
      subtitle: 'Investors pledge',
      description: 'Investors choose an amount and submit. Soft commitments are non-binding until you lock the deal.',
    },
    {
      title: 'Lock',
      subtitle: 'Confirm the deal',
      description: 'LendSolo automatically converts commitments to loan participations and notifies everyone.',
    },
  ]

  const whyFeatures = [
    {
      title: 'No more email back-and-forth',
      description: 'Commitments are tracked automatically, with a complete audit trail. No spreadsheet, no reply-all chains.',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <polyline points="22 12 16 12 14 15 10 9 8 12 2 12" />
        </svg>
      ),
    },
    {
      title: 'Race-safe capacity',
      description: 'Row-level locking means two investors can\u2019t overcommit the same deal. First to submit, first confirmed.',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      ),
    },
    {
      title: 'Auto-converts to participations',
      description: 'When you lock, pro-rata distributions start flowing on the next payment. No manual entry, no mistakes.',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
        </svg>
      ),
    },
  ]

  return (
    <>
      <SeoHead
        title="Deal Room \u2014 Syndicate Private Loans With Your Investors | LendSolo"
        description="Post loan offerings, collect investor commitments, and lock deals \u2014 all without spreadsheets. Included in LendSolo Fund tier."
        canonicalPath="/deal-room"
        schema={{
          '@type': 'SoftwareApplication',
          name: 'LendSolo Deal Room',
          applicationCategory: 'FinanceApplication',
          operatingSystem: 'Web',
          description: 'Syndicate private loans with your investors. Post offerings, collect soft commitments, and lock deals.',
          url: 'https://lendsolo.com/deal-room',
          offers: {
            '@type': 'Offer',
            price: '99',
            priceCurrency: 'USD',
            description: 'Fund tier — $99/month',
          },
        }}
      />

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <header className="bg-white border-b border-[#e2e0d8]">
        <div className="max-w-[1140px] mx-auto px-8 h-16 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#0F1419' }}>
              <span className="text-[#34D399] font-bold text-sm font-mono">LS</span>
            </div>
            <span className="text-lg font-bold text-[#0f1a2e]">LendSolo</span>
          </Link>
          <nav className="flex items-center gap-3">
            <Link href="/blog" className="hidden sm:inline-block text-sm text-[#5a6578] hover:text-[#0f1a2e] px-3 py-2">
              Blog
            </Link>
            <Link href="/#pricing" className="hidden sm:inline-block text-sm text-[#5a6578] hover:text-[#0f1a2e] px-3 py-2">
              Pricing
            </Link>
            <Link href="/users/sign_in" className="text-sm text-[#5a6578] hover:text-[#0f1a2e] px-3 py-2">
              Sign In
            </Link>
            <Link
              href="/users/sign_up?plan=fund"
              className="text-sm font-semibold text-white bg-[#1a7a4c] hover:bg-[#15673f] px-5 py-2.5 rounded-lg transition-colors"
            >
              Start Fund trial
            </Link>
          </nav>
        </div>
      </header>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden pt-20 sm:pt-28 pb-16 sm:pb-24" style={{ backgroundColor: '#f8f7f4' }}>
        <div
          className="absolute top-[-40%] left-1/2 -translate-x-1/2 w-[800px] h-[800px] pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(26,122,76,0.06) 0%, transparent 70%)' }}
        />

        <div className="relative max-w-[1140px] mx-auto px-8 text-center">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-5">
            <span className="px-2.5 py-0.5 rounded-full bg-[#e8f5ee] text-[10px]">Fund tier</span>
            Deal Room
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-[56px] leading-[1.1] text-[#0f1a2e] max-w-[700px] mx-auto tracking-tight">
            Syndicate deals without the{' '}
            <em className="italic text-[#1a7a4c]">spreadsheet chaos</em>
          </h1>

          <p className="mt-6 text-lg leading-relaxed text-[#5a6578] max-w-[560px] mx-auto">
            Post a loan to your investors, collect soft commitments, and lock the deal &mdash; all in one place. No emails, no double-entry, no lost track of who&rsquo;s in.
          </p>

          <div className="mt-10 flex gap-3 justify-center flex-wrap">
            <Link
              href="/users/sign_up?plan=fund"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#0f1a2e] text-white rounded-[10px] text-[15px] font-semibold hover:-translate-y-0.5 hover:shadow-lg transition-all"
            >
              Start 14-day Fund trial
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10m0 0L9 4m4 4L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
            <Link
              href="/#pricing"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-transparent text-[#0f1a2e] rounded-[10px] text-[15px] font-semibold border-[1.5px] border-[#e2e0d8] hover:border-[#5a6578] hover:bg-white transition-all"
            >
              See pricing
            </Link>
          </div>

          <p className="mt-5 text-[13px] text-[#8c95a6]">
            Built for micro-lenders managing other people&rsquo;s money.
          </p>
        </div>
      </section>

      {/* ── Mockup #1: Lender Deal Room view ───────────────────────────── */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-[1140px] mx-auto px-8">
          <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
            The lender view
          </p>
          <h2 className="font-display text-3xl sm:text-[40px] leading-[1.15] text-[#0f1a2e] tracking-tight mb-4">
            One page, the whole deal
          </h2>
          <p className="text-[17px] leading-relaxed text-[#5a6578] max-w-[600px] mb-10">
            After you post a deal, this is what you see &mdash; offering details, a live capacity bar, and commitments rolling in. Everything in one place, nothing to cross-reference.
          </p>

          <MockupLenderView />
        </div>
      </section>

      {/* ── Lifecycle steps ─────────────────────────────────────────────── */}
      <section className="py-20 sm:py-24" style={{ backgroundColor: '#f8f7f4' }}>
        <div className="max-w-[1140px] mx-auto px-8">
          <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
            How it works
          </p>
          <h2 className="font-display text-3xl sm:text-[40px] leading-[1.15] text-[#0f1a2e] tracking-tight mb-12">
            Four steps. One workflow.
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {lifecycleSteps.map((step, i) => (
              <div
                key={step.title}
                className="bg-white border border-[#e2e0d8] rounded-xl p-7 flex flex-col gap-4"
              >
                <div className="flex items-center gap-3">
                  <StepIcon step={i + 1} />
                  <div>
                    <p className="text-[11px] font-mono uppercase tracking-wider text-[#8c95a6]">Step {i + 1}</p>
                    <h3 className="text-base font-semibold text-[#0f1a2e]">{step.title}</h3>
                  </div>
                </div>
                <p className="text-sm font-medium text-[#1a7a4c]">{step.subtitle}</p>
                <p className="text-sm leading-relaxed text-[#5a6578]">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Mockup #2: Invitation email ─────────────────────────────────── */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-[1140px] mx-auto px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-12 items-center">
            <div>
              <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
                Magic-link access
              </p>
              <h2 className="font-display text-3xl sm:text-[36px] leading-[1.15] text-[#0f1a2e] tracking-tight mb-4">
                Your investors don&rsquo;t need an account
              </h2>
              <p className="text-[17px] leading-relaxed text-[#5a6578]">
                They get a magic-link email &mdash; one tap to the deal page. No passwords to remember, no app to download, no friction between your deal and their commitment.
              </p>
            </div>
            <MockupInvitationEmail />
          </div>
        </div>
      </section>

      {/* ── Mockup #3: Investor deal page ──────────────────────────────── */}
      <section className="py-20 sm:py-24" style={{ backgroundColor: '#f8f7f4' }}>
        <div className="max-w-[1140px] mx-auto px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
            <MockupInvestorView />
            <div>
              <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
                The investor view
              </p>
              <h2 className="font-display text-3xl sm:text-[36px] leading-[1.15] text-[#0f1a2e] tracking-tight mb-4">
                They see a clean deal page. You see soft commitments roll in.
              </h2>
              <p className="text-[17px] leading-relaxed text-[#5a6578]">
                Investors see the offering details, remaining capacity, and a simple commit form. You see who&rsquo;s pledged what &mdash; in real time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Why lenders use it ──────────────────────────────────────────── */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-[1140px] mx-auto px-8">
          <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
            Why it matters
          </p>
          <h2 className="font-display text-3xl sm:text-[40px] leading-[1.15] text-[#0f1a2e] tracking-tight mb-12">
            Built for how deals actually work
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {whyFeatures.map((f) => (
              <div
                key={f.title}
                className="bg-white border border-[#e2e0d8] rounded-xl p-8 flex gap-4"
              >
                <div className="w-11 h-11 min-w-[44px] rounded-[10px] bg-[#e8f5ee] text-[#1a7a4c] flex items-center justify-center">
                  {f.icon}
                </div>
                <div>
                  <h3 className="text-base font-semibold text-[#0f1a2e] mb-1.5">{f.title}</h3>
                  <p className="text-sm leading-relaxed text-[#5a6578]">{f.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ──────────────────────────────────────────────────── */}
      <section className="py-20 sm:py-28" style={{ backgroundColor: '#f8f7f4' }}>
        <div className="max-w-[1140px] mx-auto px-8 text-center">
          <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
            Ready?
          </p>
          <h2 className="font-display text-3xl sm:text-[40px] leading-[1.15] text-[#0f1a2e] tracking-tight max-w-[600px] mx-auto mb-4">
            Ready to run your next deal like a fund?
          </h2>
          <div className="mt-9">
            <Link
              href="/users/sign_up?plan=fund"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#0f1a2e] text-white rounded-[10px] text-[15px] font-semibold hover:-translate-y-0.5 hover:shadow-lg transition-all"
            >
              Start 14-day Fund trial
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10m0 0L9 4m4 4L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
          </div>
          <p className="mt-5 text-[13px] text-[#8c95a6]">
            $99/month. Cancel anytime. No setup fees.
          </p>
        </div>
      </section>

      <MarketingFooter />
    </>
  )
}
